/* ••[1]••••••••••••••••••••••••• EventModel.ts •••••••••••••••••••••••••••••• */

/* ••[2]•••••••••• Interfaces ••••••••••••••• */
import { CategoryJsonPage } from "../core/interfaces/JsonPage";
import Event, { EventPaginated } from "../core/interfaces/Event";
import { SortOption } from "../core/interfaces/Pagination";

/* ••[2]•••••••••• Core ••••••••••••••• */
import { EventsProvider, ProviderConfiguration } from "../core/types/Provider";
import { CommunityProviderType } from "../core/types/Provider";
import ProviderFactory from "../core/ProviderFactory";
import CategoryModel from "./CategoryModel";
import { isMode } from "../core/utilities";

export default abstract class EventModel {
  private static eventsCache: Map<string, Array<Event> | EventPaginated> =
    new Map();
  private static provider: EventsProvider = ProviderFactory.create(
    CommunityProviderType.Events,
    isMode("development")
      ? ProviderConfiguration.local
      : ProviderConfiguration.vanilla
  );

  /* ••[2]•••••••••• getEventsCached ••••••••••••••• */
  public static getEventsCached(
    dateStart: string,
    dateEnd: string,
    key: string,
    sort = SortOption.asc,
    pageSize = -1,
    page = -1,
    categoryID = -1
  ): Promise<Array<Event> | EventPaginated> {
    let cacheKey = key;
    const isOngoing =
      key &&
      (key.toLowerCase().includes("ongoing") ||
        (dateStart.startsWith("<") && dateEnd.startsWith(">")));
    if (isOngoing) {
      const match = key.match(/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/);
      let normalizedDate = "";
      if (match) {
        normalizedDate = match[0].slice(0, 13);
      }
      // Stable cache key for ongoing events
      cacheKey = `ongoing-events-${normalizedDate}-${categoryID}-${sort}-${pageSize}-${page}`;
    }

    return new Promise((resolve, reject) => {
      if (!EventModel.eventsCache.has(cacheKey)) {
        EventModel.getCategoryID(categoryID)
          .then((categoryID: number) => {
            if (categoryID == 585 && isOngoing) {
              categoryID = 0;
            }
            return EventModel.getEvents(
              dateStart,
              dateEnd,
              categoryID,
              pageSize,
              sort,
              page
            );
          })
          .then((result: Array<Event> | EventPaginated): void => {
            EventModel.eventsCache.set(cacheKey, result);
            resolve(result);
          })
          .catch((error) => {
            reject(error);
          });
      } else {
        resolve(EventModel.eventsCache.get(cacheKey));
      }
    });
  }

  private static getCategoryID(categoryID: number): Promise<number> {
    if (categoryID === -1) {
      return EventModel.getCategoryFromJsonPage().then((metadata) => {
        return new Promise((resolve) => {
          if (metadata && metadata.Category) {
            resolve(metadata.Category.CategoryID);
          } else {
            resolve(0);
          }
        });
      });
    }
    return new Promise((resolve) => {
      resolve(categoryID);
    });
  }

  /* ••[2]•••••••••• getCategoryFromJsonPage ••••••••••••••• */
  private static getCategoryFromJsonPage(): Promise<CategoryJsonPage | null> {
    return EventModel.provider.getCategoryFromJsonPage();
  }

  /* ••[2]•••••••••• getEvents ••••••••••••••• */
  private static getEvents(
    dateStart: string,
    dateEnd: string,
    categoryID: number,
    limit = -1,
    sort = SortOption.asc,
    page = -1
  ): Promise<Array<Event> | EventPaginated> {
    return this.provider.getEvents(
      dateStart,
      dateEnd,
      categoryID,
      sort,
      page > 0,
      limit,
      page
    );
  }

  /* ••[2]•••••••••• rsvpEvent ••••••••••••••• */
  public static rsvpEvent(
    rsvpOption: string,
    eventID: number
  ): Promise<boolean> {
    return EventModel.provider.rsvpEvent(rsvpOption, eventID);
  }

  /* ••[2]•••••••••• isEventsCategoryPage ••••••••••••••• */
  public static async isEventsCategoryPage(): Promise<boolean> {
    const categoryJsonPage: CategoryJsonPage | null =
      await CategoryModel.getCategoryFromJsonPageCached();

    if (!categoryJsonPage || !categoryJsonPage.Category) {
      return false;
    }

    try {
      const events: Array<Event> = (await EventModel.getEventsCached(
        "",
        "",
        `cat${categoryJsonPage.Category.CategoryID}`,
        SortOption.asc,
        1,
        -1,
        categoryJsonPage.Category.CategoryID
      )) as Array<Event>;

      return !!events.length;
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  public static async isGlobalEventsCategoryPage(): Promise<boolean> {
    const categoryJsonPage: CategoryJsonPage | null =
      await CategoryModel.getCategoryFromJsonPageCached();

    if (!categoryJsonPage || !categoryJsonPage.Category) {
      return false;
    }

    return categoryJsonPage.Category.UrlCode === "all-events";
  }

  public static async isOnDemandPage(): Promise<boolean> {
    try {
      const category = await this.getCategoryFromJsonPage();
      return (
        category?.Category?.UrlCode?.toLowerCase() === "on-demand" ||
        window.location.href
          .toLowerCase()
          .includes("events-ondemand-oraclego-cloudsolutions")
      );
    } catch (error) {
      console.error("Error checking if page is On-Demand:", error);
      return false;
    }
  }
}
